import { apiRequest } from './httpRequest';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { MatchCategory } from './moleculeSearch';

/** #704 -- the box a query molecule sits in on the paper. */
export type ReactionQueryRole = 'reactant' | 'product' | 'agent' | 'solvent';

/** #704 -- a combined reaction query: the molecules in each box, and optional arrow kinds. */
export interface ReactionSearchQuery {
  readonly reactants?: readonly ChemMoleculeV1Document[];
  readonly products?: readonly ChemMoleculeV1Document[];
  readonly agents?: readonly ChemMoleculeV1Document[];
  readonly solvents?: readonly ChemMoleculeV1Document[];
  readonly arrowKinds?: readonly string[];
}

/** Why a reaction scored: query molecule `queryIndex` (in its box) matched a participant. */
export interface ReactionSearchMatch {
  readonly queryRole: ReactionQueryRole;
  readonly queryIndex: number;
  readonly participantRole: string;
  readonly moleculeId: string;
  readonly category: MatchCategory;
  readonly contribution: number;
}

export interface ReactionSearchHit {
  readonly reactionId: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly arrowKind: string | null;
  /** 0..1, role-aware (docs/reaction-search.md). */
  readonly score: number;
  readonly matches: readonly ReactionSearchMatch[];
  /** #1136 -- false for someone else's public reaction: read-only, add it to your paper. */
  readonly owned?: boolean;
}

export interface ReactionSearchResults {
  readonly reactions: readonly ReactionSearchHit[];
}

/** #704 -- ranked reactions for a combined query (the boxes on the paper). */
export async function searchReactions(query: ReactionSearchQuery): Promise<ReactionSearchResults> {
  return apiRequest<ReactionSearchResults>('/search/reactions', 'reaction search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(query)
  });
}
